import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { RefreshCw, Download, X, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PageContainer, PageHeader } from '../components/layout/Page';
import { formatPersonName } from '../utils/formatName';

export type ReportCellKind = 'future' | 'current' | 'no_data' | 'zero' | 'value';

/**
 * Presentation-only classification of a report cell. Does not change how the
 * completion value is computed: the current month is still excluded from the
 * numbers, it is just labelled as "en curso" instead of looking like the future.
 */
export function reportCellKind(selectedYear: number, monthIndex: number, value: number | null, now = new Date()): ReportCellKind {
  const year = now.getFullYear();
  const month = now.getMonth();
  if (selectedYear > year || (selectedYear === year && monthIndex > month)) return 'future';
  if (selectedYear === year && monthIndex === month) return 'current';
  if (value == null) return 'no_data';
  return value === 0 ? 'zero' : 'value';
}

interface UserProgressData {
  user_id: string;
  user_email: string;
  user_full_name: string;
  organization_name: string;
  organization_color: string;
  // Completion percentages (horizontal view - payment threshold) - database returns as string
  january_completion: number | string;
  february_completion: number | string;
  march_completion: number | string;
  april_completion: number | string;
  may_completion: number | string;
  june_completion: number | string;
  july_completion: number | string;
  august_completion: number | string;
  september_completion: number | string;
  october_completion: number | string;
  november_completion: number | string;
  december_completion: number | string;
}

const MONTHS = [
  { key: 'january', label: 'ENE', name: 'Enero' },
  { key: 'february', label: 'FEB', name: 'Febrero' },
  { key: 'march', label: 'MAR', name: 'Marzo' },
  { key: 'april', label: 'ABR', name: 'Abril' },
  { key: 'may', label: 'MAY', name: 'Mayo' },
  { key: 'june', label: 'JUN', name: 'Junio' },
  { key: 'july', label: 'JUL', name: 'Julio' },
  { key: 'august', label: 'AGO', name: 'Agosto' },
  { key: 'september', label: 'SEP', name: 'Septiembre' },
  { key: 'october', label: 'OCT', name: 'Octubre' },
  { key: 'november', label: 'NOV', name: 'Noviembre' },
  { key: 'december', label: 'DIC', name: 'Diciembre' }
];

export default function AdminReport() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [reportData, setReportData] = useState<UserProgressData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [refreshing, setRefreshing] = useState(false);
  const [organizationFilter, setOrganizationFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const fetchReportData = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    setReportData([]);
    try {
      const data = await api.getAdminReportData(selectedYear);
      if (id === request.current) setReportData(data as unknown as UserProgressData[]);
    } catch {
      if (id === request.current) setError('No se pudo cargar el reporte. Intenta actualizar de nuevo.');
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (userProfile?.role !== 'admin') return;
    fetchReportData();
    const subscription = api.subscribeToProgressUpdates(fetchReportData);
    return () => { request.current++; subscription.unsubscribe(); };
  }, [fetchReportData, userProfile?.role]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // First refresh the mappings and calculations
      await api.refreshDocumentPeriodMappings();
      await api.recalculateUserProgress(undefined, selectedYear);
      // Then fetch fresh data
      await fetchReportData();
    } catch (error) {
      setError('No se pudo actualizar el reporte. Intenta de nuevo.');
    } finally {
      setRefreshing(false);
    }
  };

  const getPercentageValue = (user: UserProgressData, monthKey: string): number | null => {
    const value = user[`${monthKey}_completion` as keyof UserProgressData];
    const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : null;
  };

  const getPercentageColor = (percentage: number | null): string => {
    if (percentage == null) return 'text-slate-400 border border-dashed border-slate-200 bg-transparent';
    if (percentage === 0) return 'text-red-800 bg-red-100 ring-1 ring-inset ring-red-200';
    if (percentage >= 90) return 'text-emerald-800 bg-emerald-50';
    if (percentage >= 70) return 'text-lime-800 bg-lime-50';
    if (percentage >= 50) return 'text-yellow-800 bg-yellow-50';
    if (percentage >= 30) return 'text-orange-800 bg-orange-50';
    return 'text-red-800 bg-red-50';
  };

  const handleCellClick = (userId: string, monthIndex: number) => {
    // Navigate to user detail page with selectedMonth parameter to show list view
    const monthParam = `${selectedYear}-${String(monthIndex + 1).padStart(2, '0')}`;
    navigate(`/review/${userId}?${new URLSearchParams({ selectedMonth: monthParam, returnTo: '/report' })}`);
  };

  const formatPercentage = (value: number | null, monthIndex: number): string => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based (January = 0)

    // Hide current month and future months
    // Show up to last completed month only
    if (selectedYear > currentYear || (selectedYear === currentYear && monthIndex >= currentMonth)) {
      return '';
    }

    return value == null ? '—' : `${Math.round(value)}%`;
  };

  const getOrganizations = () => {
    const orgs = new Set(reportData.map(user => user.organization_name).filter(Boolean));
    return Array.from(orgs).sort();
  };

  const filteredData = reportData.filter(user => {
    const matchesOrg = !organizationFilter || user.organization_name === organizationFilter;
    const matchesSelectedUsers = selectedUserIds.length === 0 || selectedUserIds.includes(user.user_id);
    return matchesOrg && matchesSelectedUsers;
  });

  const availableUsers = reportData.filter(user => {
    const matchesSearch = !searchTerm ||
      user.user_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const notSelected = !selectedUserIds.includes(user.user_id);
    return matchesSearch && notSelected;
  });

  const toggleUserSelection = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const clearUserSelection = () => {
    setSelectedUserIds([]);
  };

  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`Cumplimiento documental ${selectedYear}`, pageWidth / 2, 20, { align: 'center' });

    // Subtitle
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const subtitle = 'Meses completos';
    doc.text(subtitle, pageWidth / 2, 28, { align: 'center' });

    // Date generated
    doc.setFontSize(10);
    doc.text(`Generado el: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}`, pageWidth - 20, 15, { align: 'right' });

    // Prepare table data
    const headers = ['Usuario', 'Organización', 'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const tableData = filteredData.map(user => [
      formatPersonName(user.user_full_name) || user.user_email,
      user.organization_name || 'Sin organización',
      ...MONTHS.map((month, index) => formatPercentage(getPercentageValue(user, month.key), index))
    ]);

    // Add table with autoTable
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
      },
      headStyles: {
        fillColor: [59, 130, 246], // Blue background
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 40 }, // Usuario
        1: { halign: 'left', cellWidth: 35 }, // Organización
        // Months columns (2-13)
        2: { cellWidth: 15 }, 3: { cellWidth: 15 }, 4: { cellWidth: 15 }, 5: { cellWidth: 15 },
        6: { cellWidth: 15 }, 7: { cellWidth: 15 }, 8: { cellWidth: 15 }, 9: { cellWidth: 15 },
        10: { cellWidth: 15 }, 11: { cellWidth: 15 }, 12: { cellWidth: 15 }, 13: { cellWidth: 15 },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250], // Light gray for alternate rows
      },
      didParseCell: function(data: any) {
        if (data.section !== 'body' || data.column.index < 2) return;
        const value = data.cell.text[0];
        const percentage = Number(String(value).replace('%', ''));
        data.cell.styles.textColor = [71, 85, 105];
        if (!value || value === '—' || !Number.isFinite(percentage)) data.cell.styles.fillColor = [248, 250, 252];
        else if (percentage === 0) { data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [153, 27, 27]; }
        else if (percentage >= 90) { data.cell.styles.fillColor = [236, 253, 245]; data.cell.styles.textColor = [6, 95, 70]; }
        else if (percentage >= 70) data.cell.styles.fillColor = [247, 254, 231];
        else if (percentage >= 50) data.cell.styles.fillColor = [254, 252, 232];
        else if (percentage >= 30) data.cell.styles.fillColor = [255, 247, 237];
        else data.cell.styles.fillColor = [254, 242, 242];
      },
      margin: { top: 35, left: 10, right: 10 },
    });

    // Footer info
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Total de usuarios: ${filteredData.length}`, 20, pageHeight - 10);
    doc.text('REPSE Platform - Reporte generado automáticamente', pageWidth - 20, pageHeight - 10, { align: 'right' });

    // Save the PDF
    doc.save(`reporte-cumplimiento-${selectedYear}.pdf`);
  };

  if (userProfile?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h1>
          <p className="text-gray-600">No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <PageContainer width="wide">
        <PageHeader
          title="Cumplimiento documental"
          description="Porcentaje de requisitos cumplidos por usuario en cada mes completo."
          actions={<>
            <button onClick={handleRefresh} disabled={refreshing || loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin motion-reduce:animate-none' : ''}`} />Actualizar</button>
            <button onClick={exportToPDF} disabled={loading || refreshing || !!error || !filteredData.length} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50"><Download className="h-4 w-4" />Exportar PDF</button>
          </>}
        />
        <section aria-label="Filtros del reporte" className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-600">Año<select aria-label="Año del reporte" value={selectedYear} disabled={refreshing} onChange={event => setSelectedYear(Number(event.target.value))} className="mt-1 block rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900">{Array.from({length:Math.max(new Date().getFullYear(),selectedYear)-2015+2},(_,i)=>2015+i).reverse().map(year => <option key={year} value={year}>{year}</option>)}</select></label>
              {/* User Multi-Select Filter */}
              <div className="min-w-0 flex-1 basis-64">
                <div className="relative" ref={dropdownRef} onKeyDown={event => {if (event.key === 'Escape') {setShowUserDropdown(false); dropdownRef.current?.querySelector('button')?.focus();}}}>
                  <button
                    aria-expanded={showUserDropdown} aria-controls="report-user-options" onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {selectedUserIds.length === 0
                        ? 'Seleccionar usuarios...'
                        : `${selectedUserIds.length} usuario${selectedUserIds.length > 1 ? 's' : ''} seleccionado${selectedUserIds.length > 1 ? 's' : ''}`
                      }
                    </span>
                  </button>

                  {/* Dropdown */}
                  {showUserDropdown && (
                    <div id="report-user-options" className="absolute z-50 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                      <div className="p-3 border-b border-gray-200">
                        <input
                          type="text"
                          aria-label="Buscar usuarios" placeholder="Buscar usuarios..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {availableUsers.length > 0 ? (
                          availableUsers.slice(0, 50).map(user => (
                            <button
                              key={user.user_id}
                              onClick={() => {
                                toggleUserSelection(user.user_id);
                                setSearchTerm('');
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100"
                            >
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                                style={{ backgroundColor: user.organization_color || '#6B7280' }}
                              >
                                {user.user_full_name?.charAt(0)?.toUpperCase() || user.user_email?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {formatPersonName(user.user_full_name) || 'Sin nombre'}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{user.user_email}</div>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center text-sm text-gray-500">
                            {searchTerm ? 'No se encontraron usuarios' : 'Todos los usuarios ya están seleccionados'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="min-w-0 flex-1 basis-48">
                <select aria-label="Organización"
                  value={organizationFilter}
                  onChange={(e) => setOrganizationFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas las organizaciones</option>
                  {getOrganizations().map(org => (
                    <option key={org} value={org}>{org}</option>
                  ))}
                </select>
              </div>

          </div>

            {/* Selected Users Chips */}
            {selectedUserIds.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {reportData
                  .filter(user => selectedUserIds.includes(user.user_id))
                  .map(user => (
                    <div
                      key={user.user_id}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                    >
                      <span className="truncate max-w-[200px]">
                        {formatPersonName(user.user_full_name) || user.user_email}
                      </span>
                      <button
                        aria-label={`Quitar ${formatPersonName(user.user_full_name) || user.user_email}`} onClick={() => toggleUserSelection(user.user_id)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                <button
                  onClick={clearUserSelection}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium hover:bg-gray-200"
                >
                  <X className="w-3 h-3" />
                  Limpiar todo
                </button>
              </div>
            )}
        </section>
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-white p-4 text-sm text-red-800">{error} <button className="underline" onClick={fetchReportData}>Reintentar</button></p>}
        <section aria-label="Cumplimiento por usuario y mes" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-600">Cargando datos...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-sm"><caption className="sr-only">Cumplimiento {selectedYear}. Solo meses completos; el mes en curso se marca como en curso. Un guion indica datos no disponibles.</caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 sticky left-0 bg-slate-50 z-10 w-64">
                      Usuario
                    </th>
                    {MONTHS.map((month, monthIndex) => {
                      const current = reportCellKind(selectedYear, monthIndex, null) === 'current';
                      return (
                        <th key={month.key} scope="col" className={`px-1 py-3 text-center text-xs font-medium w-14 ${current ? 'text-blue-800' : 'text-gray-500'}`}>
                          {month.label}
                          {current && <span className="mt-1 block rounded-full bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-800 ring-1 ring-inset ring-blue-200">En curso</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((user, index) => (
                    <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                      <th scope="row" className="sticky left-0 z-10 w-64 max-w-[256px] border-r border-slate-100 bg-white px-4 py-3 text-left font-normal">
                        <p className="break-words text-sm font-medium text-slate-900">{formatPersonName(user.user_full_name) || user.user_email}</p>
                        <p className="mt-1 break-all text-xs text-slate-500">{user.user_email}</p>
                        <p className="text-xs text-slate-500">{user.organization_name || 'Sin organización'}</p>
                      </th>
                      {MONTHS.map((month, monthIndex) => {
                        const percentage = getPercentageValue(user, month.key);
                        const formattedPercentage = formatPercentage(percentage, monthIndex);
                        const kind = reportCellKind(selectedYear, monthIndex, percentage);
                        const isFutureOrCurrent = kind === 'future' || kind === 'current';
                        const tone = kind === 'future' ? 'text-slate-300' : kind === 'current' ? 'report-cell-current text-blue-800' : getPercentageColor(percentage);
                        const status = kind === 'future' ? 'Mes no completado' : kind === 'current' ? 'Mes en curso, aún sin cierre' : percentage == null ? 'Sin datos' : formattedPercentage;

                        return (
                          <td key={month.key} className="px-1 py-1">
                            <button type="button" disabled={isFutureOrCurrent || percentage == null} onClick={() => handleCellClick(user.user_id, monthIndex)}
                              title={kind === 'current' ? 'Mes en curso: el porcentaje se calcula al cerrar el mes' : kind === 'no_data' ? 'Sin datos para este mes' : undefined}
                              className={`h-10 w-full rounded-md text-xs font-medium tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${tone} enabled:hover:ring-1 enabled:hover:ring-slate-300`}
                              aria-label={`${formatPersonName(user.user_full_name) || user.user_email}, ${month.name} ${selectedYear}: ${status}`}>
                              {kind === 'current' ? <span aria-hidden="true" className="text-[10px]">En curso</span> : isFutureOrCurrent ? '' : formattedPercentage}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredData.length > 0 && (
                <ul aria-label="Leyenda del reporte" className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-200 px-4 py-3 text-xs text-slate-600">
                  <li className="flex items-center gap-2"><span aria-hidden="true" className="inline-flex h-5 w-9 items-center justify-center rounded text-[10px] text-emerald-800 bg-emerald-50">90%</span>Cumplimiento del mes</li>
                  <li className="flex items-center gap-2"><span aria-hidden="true" className="inline-flex h-5 w-9 items-center justify-center rounded text-[10px] text-red-800 bg-red-100 ring-1 ring-inset ring-red-200">0%</span>Ningún requisito cumplido</li>
                  <li className="flex items-center gap-2"><span aria-hidden="true" className="inline-flex h-5 w-9 items-center justify-center rounded text-[10px] text-slate-400 border border-dashed border-slate-200">—</span>Sin datos</li>
                  <li className="flex items-center gap-2"><span aria-hidden="true" className="report-cell-current inline-flex h-5 w-9 rounded" />Mes en curso (se calcula al cerrar)</li>
                </ul>
              )}
              {filteredData.length === 0 && !loading && !error && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No se encontraron usuarios</p>
                </div>
              )}
            </div>
          )}
        </section>
    </PageContainer>
  );
}
